import React, { useEffect, useRef, useState } from "react";
import {
  Layout as AntdLayout,
  Button,
  Divider,
  Dropdown,
  theme,
  Breadcrumb,
  Typography,
} from "antd";
import {
  KeyOutlined,
  LeftOutlined,
  LogoutOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import clsx from "clsx";
import { useMediaQuery } from "react-responsive";
import { NavLink } from "react-router";
import Menu from "./Menu";
import ReduxRedirect from "../Common/ReduxRedirect";
import { LogoMark } from "../Common/Icon";
import { BRAND_NAME } from "../../resource/brand";
import LanguageSwitcher from "../Common/LanguageSwitcher";
import { signOut } from "@triare/auth-redux/dist/saga/auth/signOut";
import { useTranslation } from "react-i18next";
import store from "../../store";
import { useAuth } from "../../store/auth";
import NotificationDropdown from "../Common/NotificationMenu";
import useDangerMenuItem from "../Common/DangerMenuItem";
import { PREVIEW_NOTIFICATIONS } from "../Common/NotificationMenu/mock";
import { BreadcrumbsProvider, useBreadcrumbs } from "../../context/breadcrumbs";

import styles from "./index.module.scss";
import { useScrolledUnder } from "../Common/StickyBar";

const { Sider, Header, Content } = AntdLayout; // Header

interface LayoutProps {
  LeftMenu?: React.FC;
  leftMenu?: React.ReactNode;
  transparent?: boolean;
  disableLeftMenu?: boolean;
  children: React.ReactNode;
  collapsedByDefault?: boolean;
  minWidth?: boolean;
  title?: string;
}

let defaultStateCollapsed = false;

const LayoutBreadcrumbs = () => {
  const { breadcrumbs } = useBreadcrumbs();
  return (
    <Breadcrumb style={{ marginBottom: 0 }}>
      {breadcrumbs.map((item, index) => (
        <Breadcrumb.Item key={index}>
          {item.href ? (
            <NavLink to={item.href}>{item.title}</NavLink>
          ) : (
            item.title
          )}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};

function Layout({
  disableLeftMenu = false,
  LeftMenu,
  leftMenu = <Menu />,
  transparent,
  children,
  collapsedByDefault = false,
  minWidth,
  title,
}: LayoutProps): React.ReactNode {
  const [collapsed, setCollapsed] = useState(defaultStateCollapsed);
  // The bell's contents, until a developer connects it.
  const [notifications, setNotifications] = useState(PREVIEW_NOTIFICATIONS);
  // The header stays put while the content scrolls under it; the shadow only
  // appears once there is something above the fold to cast it. Same hook as
  // every other bar of this shape in the app.
  const headerScroll = useScrolledUnder();
  const { token } = theme.useToken();
  const { colorBgContainer } = token;
  const dangerItem = useDangerMenuItem();
  const isTablet = useMediaQuery({
    query: "(max-width: 1024px)",
  });
  const disableMenu = disableLeftMenu || (!LeftMenu && !leftMenu);
  const { user } = useAuth();
  const { t } = useTranslation();

  const accountItems = [
    {
      key: "change-password",
      icon: <KeyOutlined />,
      label: t("common.changePassword"),
      // TODO(design): no change-password screen exists yet — a developer needs
      // to point this at one. See DEVELOPER_TASKS.md.
    },
    // The colour is on the icon and the label and nowhere else — see
    // Common/DangerMenuItem for why not antd's `danger` prop. This is where
    // that was worked out; it is shared now, so the campaign section marks
    // Delete the same way.
    dangerItem({
      key: "log-out",
      icon: <LogoutOutlined />,
      label: t("routes.logOut"),
      onClick: () => store.dispatch(signOut()),
    }),
  ];

  useEffect(() => {
    defaultStateCollapsed = collapsed;
  }, [collapsed]);

  // Tablet width, or a screen that asks for it, forces the menu shut. Anything
  // else is the user's call.
  const forcedCollapsed = (isTablet && !disableMenu) || collapsedByDefault;
  const wasForced = useRef(forcedCollapsed);

  useEffect(() => {
    if (forcedCollapsed) {
      setCollapsed(true);
    } else if (wasForced.current) {
      // The constraint has just lifted — a resize back to a wide window — so
      // the panel gets its width back.
      setCollapsed(false);
    }

    // On any other run this leaves the state alone. It used to call
    // setCollapsed(false) unconditionally, and since every screen renders its
    // own <Layout>, that re-opened the menu on every navigation and threw away
    // the choice the user had just made.
    wasForced.current = forcedCollapsed;
  }, [forcedCollapsed]);

  return (
    <>
      <ReduxRedirect />
      <AntdLayout hasSider className={styles.layout}>
        {disableMenu ? null : (
          <Sider
            // A light sider is a theme, not a background override: antd's
            // lightSiderBg is colorBgContainer, the white the panel had been
            // painted by hand.
            theme="light"
            trigger={null}
            collapsible
            collapsed={collapsed}
            className={clsx(styles.slider, {
              [styles.absolute]: isTablet && !disableMenu,
            })}
            width="var(--side-menu-w)"
            collapsedWidth="var(--side-menu-collapsed-w)"
          >
            {/* The logo row is the second collapse control: the panel's whole
                head is a target, which is easier to hit than the button at its
                foot. It used to link to the dashboard; that route is still one
                click away in the menu below. */}
            <button
              type="button"
              className={styles.logo}
              onClick={() => setCollapsed(!collapsed)}
              aria-expanded={!collapsed}
              aria-label={t(
                collapsed ? "common.expandMenu" : "common.collapseMenu",
              )}
            >
              <LogoMark className={styles.logoMark} />
              {!collapsed ? (
                <Typography.Title level={5} className={styles.logoName}>
                  {BRAND_NAME}
                </Typography.Title>
              ) : null}
            </button>

            <div
              className={clsx(styles.sideMenu, {
                [styles.collapsed]: collapsed,
              })}
            >
              {LeftMenu ? <LeftMenu /> : leftMenu}
            </div>

            <div className={styles.collapse}>
              <Divider className={styles.collapseDivider} />
              <Button
                type="text"
                block
                icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                aria-label={t(
                  collapsed ? "common.expandMenu" : "common.collapseMenu",
                )}
              />
            </div>
          </Sider>
        )}
        <BreadcrumbsProvider>
          <AntdLayout
            className={clsx(styles.wrapper, {
              [styles.tablet]: isTablet && !disableMenu,
            })}
          >
            <Header
              style={{ background: colorBgContainer, ...headerScroll.shadow }}
              className={clsx(styles.header)}
            >
              <LayoutBreadcrumbs />
              <div className={styles.rightMenu}>
                {/* Mock content, so the badge and the list can be seen while
                    the bell waits to be connected — see mock.ts beside the
                    component and DEVELOPER_TASKS.md. Marking one read is local
                    state here: nothing leaves the browser. */}
                <NotificationDropdown
                  notifications={notifications}
                  onToggleRead={(id, read) =>
                    setNotifications((current) =>
                      current.map((item) =>
                        item.id === id ? { ...item, read } : item,
                      ),
                    )
                  }
                  onMarkAllAsRead={() =>
                    setNotifications((current) =>
                      current.map((item) => ({ ...item, read: true })),
                    )
                  }
                />
                <Dropdown
                  menu={{ items: accountItems }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button type="text" className={styles.account}>
                    <span className={styles.userLogo}>
                      <UserOutlined />
                    </span>
                    {user?.email}
                  </Button>
                </Dropdown>
                <LanguageSwitcher />
              </div>
            </Header>
            {/*<Header
            style={{ background: colorBgContainer }}
            className={clsx(styles.header, {
              [styles.headerLogo]: disableMenu,
            })}
          >
            {disableMenu ? (
              <NavLink
                to="/"
                className={clsx(styles.logo, { [styles.tablet]: isTablet })}
              >
                {collapsed ? (
                  <img
                    src="/logo.png"
                    className={styles.logoSvg}
                    alt="Triare"
                  />
                ) : null}
                <MainLogo className={styles.logoSvg} />

                {!collapsed ? (
                  <img
                    src="/logo.png"
                    className={styles.logoSvg}
                    alt="Triare"
                  />
                ) : null}
                <MainLogo className={styles.logoSvg} />
              </NavLink>
            ) : null}

            Header items should be here:
            <Menu />
          </Header>*/}

            <div
              className={clsx(styles.scroll, {
                [styles.collapsed]: collapsed,
              })}
              onScroll={headerScroll.onScroll}
            >
              <Content
                className={clsx(
                  styles.content,
                  { [styles.transparent]: transparent },
                  { [styles.minWidth]: isTablet && minWidth },
                )}
                id="layout-content"
              >
                {title ? (
                  <div className={styles.title}>
                    <h2>{title}</h2>
                  </div>
                ) : null}
                {children}
              </Content>
            </div>
          </AntdLayout>
        </BreadcrumbsProvider>
      </AntdLayout>
    </>
  );
}

export default Layout;
